import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  SimpleGrid,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

// Tool card data
const tools = [
  {
    id: 'dns',
    name: 'DNS Detection Tool',
    description: 'Look up domain registrar, DNS provider, and get step-by-step access instructions.',
    icon: () => (
      <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    path: '/dns',
    color: 'purple',
  },
  {
    id: 'seo',
    name: 'SEO Meta Scraper',
    description: 'Bulk scrape page titles, meta descriptions, H1 tags, and more from multiple URLs.',
    icon: () => (
      <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10,9 9,9 8,9" />
      </svg>
    ),
    path: '/seo-scraper',
    color: 'pink',
  },
  {
    id: 'launch-checklist',
    name: 'Launch Checklist',
    description: 'Audit websites for essential elements like SEO tags, tracking scripts, and performance.',
    icon: () => (
      <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
    path: '/launch-checklist',
    color: 'purple',
  },
  {
    id: 'meeting-notes',
    name: 'Meeting Notes Parser',
    description: 'Parse meeting transcripts into organized action items for content, dev, design, and client follow-ups.',
    icon: () => (
      <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
      </svg>
    ),
    path: '/meeting-notes',
    color: 'pink',
  },
];

function ToolCard({ tool }) {
  const navigate = useNavigate();

  return (
    <MotionBox
      bg="#1A1F2E"
      p={8}
      borderRadius="xl"
      borderWidth="1px"
      borderColor="gray.700"
      cursor="pointer"
      onClick={() => navigate(tool.path)}
      whileHover={{
        scale: 1.02,
        borderColor: tool.color === 'purple' ? '#7c3aed' : '#ec4899',
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
      _hover={{
        boxShadow: tool.color === 'purple'
          ? '0 0 30px rgba(124, 58, 237, 0.3)'
          : '0 0 30px rgba(236, 72, 153, 0.3)',
      }}
    >
      <VStack align="start" spacing={4}>
        <Box
          p={3}
          borderRadius="lg"
          bg={tool.color === 'purple' ? 'purple.900' : 'pink.900'}
          color={tool.color === 'purple' ? 'purple.300' : 'pink.300'}
        >
          <tool.icon />
        </Box>
        <Heading size="md" color="white" fontWeight="bold">
          {tool.name}
        </Heading>
        <Text color="gray.400" fontSize="sm" lineHeight="tall">
          {tool.description}
        </Text>
      </VStack>
    </MotionBox>
  );
}

function Home() {
  return (
    <VStack spacing={12} align="stretch" maxW="1000px" mx="auto" pt={12} px={4}>
      {/* Header */}
      <Box textAlign="center">
        <Heading
          as="h1"
          fontSize={{ base: '3xl', md: '5xl' }}
          fontWeight="bold"
          color="white"
          mb={4}
          bgGradient="linear(to-r, purple.400, pink.400)"
          bgClip="text"
        >
          Ferocious Website Tools
        </Heading>
        <Text fontSize="lg" color="gray.400" fontWeight="medium">
          Internal Tools - Ferocious Media
        </Text>
      </Box>

      {/* Tools Grid */}
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        {tools.map((tool) => (
          <ToolCard key={tool.id} tool={tool} />
        ))}
      </SimpleGrid>

      {/* Footer */}
      <Text textAlign="center" color="gray.600" fontSize="sm" pb={8}>
        Select a tool to get started
      </Text>
    </VStack>
  );
}

export default Home;
